// src/components/listing/specialized/ServicesForm.jsx
import React from 'react';
import Input from '../../../common/Input';
import Dropdown from '../../../common/Dropdown';

const ServicesForm = ({ form, errors, submitting, onFieldChange, categoryId }) => {
  
  // Determine service type based on category ID
  const getServiceCategory = () => {
    if (!categoryId) return 'general';
    
    const categoryNum = parseInt(categoryId);
    // console.log('🔧 Service category ID:', categoryNum);
    
    switch(categoryNum) {
      case 85: return 'repair'; // Repair & Maintenance
      case 86: return 'cleaning'; // Home Cleaning & Laundry
      case 87: return 'pest'; // Pest Control
      case 88: return 'event'; // Event Planning & Photography
      case 89: return 'health'; // Health & Fitness
      case 90: return 'education'; // Education & Tutoring
      case 91: return 'logistics'; // Logistics & Delivery
      case 92: return 'business'; // Business & Tech Services
      case 93: return 'legal'; // Legal & Financial Services
      case 94: return 'plumbing'; // Plumbing & Water Treatment
      default: return 'general';
    }
  };

  const serviceCategory = getServiceCategory();
  // console.log('🎯 Service category:', serviceCategory);

  const renderSimpleItem = (item) => (
    <div className="p-3 hover:bg-gray-50 cursor-pointer">
      <span className="text-gray-900">{item.name || item.label}</span>
    </div>
  );

  // Get qualification placeholder based on service category
  const getQualificationPlaceholder = () => {
    switch(serviceCategory) {
      case 'repair': return 'e.g., Certified Electrician, Licensed Plumber';
      case 'cleaning': return 'e.g., Professional Cleaning Certification';
      case 'health': return 'e.g., Certified Trainer, Medical License';
      case 'education': return 'e.g., B.Sc, Teaching Certificate, PhD';
      case 'legal': return 'e.g., Bar Certificate, Financial Advisor License';
      case 'business': return 'e.g., IT Certification, Business Degree';
      case 'event': return 'e.g., Photography Diploma, Event Planning Certificate';
      case 'plumbing': return 'e.g., Licensed Plumber, Water Treatment Certification';
      default: return 'e.g., Relevant certifications or qualifications';
    }
  };

  // Common fields for all service types
  const renderCommonFields = () => (
    <>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Service Type *
        </label>
        <Dropdown
          value={form.service_type || 'individual'}
          onValueChange={(value) => onFieldChange('service_type', value)}
          items={[
            { id: 'individual', name: 'Individual', value: 'individual' },
            { id: 'company', name: 'Company', value: 'company' },
            { id: 'freelancer', name: 'Freelancer', value: 'freelancer' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Years of Experience *
        </label>
        <Input
          type="number"
          placeholder="e.g., 5"
          value={form.experience_years || ''}
          onChange={(e) => onFieldChange('experience_years', e.target.value)}
          error={errors.experience_years}
          disabled={submitting}
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Qualification (Optional)
        </label>
        <Input
          type="text"
          placeholder={getQualificationPlaceholder()}
          value={form.qualification || ''}
          onChange={(e) => onFieldChange('qualification', e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Service Area (Address)
        </label>
        <Input
          type="text"
          placeholder="e.g., Lagos Mainland, Ikeja, Victoria Island"
          value={form.service_area || ''}
          onChange={(e) => onFieldChange('service_area', e.target.value)}
          disabled={submitting}
        />
      </div>
    </>
  );

  // Fields specific to Repair & Maintenance services
  const renderRepairFields = () => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Specialization
      </label>
      <Dropdown
        value={form.specialization || ''}
        onValueChange={(value) => onFieldChange('specialization', value)}
        items={[
          { id: 'electrical', name: 'Electrical', value: 'electrical' },
          { id: 'plumbing', name: 'Plumbing', value: 'plumbing' },
          { id: 'carpentry', name: 'Carpentry', value: 'carpentry' },
          { id: 'ac_repair', name: 'AC Repair', value: 'ac_repair' },
          { id: 'appliance', name: 'Appliance Repair', value: 'appliance' },
          { id: 'automotive', name: 'Automotive', value: 'automotive' },
          { id: 'electronics', name: 'Electronics', value: 'electronics' },
        ]}
        disabled={submitting}
        renderItem={renderSimpleItem}
      />
    </div>
  );

  // Fields specific to Plumbing & Water Treatment services
  const renderPlumbingFields = () => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Plumbing Specialization
      </label>
      <Dropdown
        value={form.specialization || ''}
        onValueChange={(value) => onFieldChange('specialization', value)}
        items={[
          { id: 'residential', name: 'Residential Plumbing', value: 'residential' },
          { id: 'commercial', name: 'Commercial Plumbing', value: 'commercial' },
          { id: 'industrial', name: 'Industrial Plumbing', value: 'industrial' },
          { id: 'water_treatment', name: 'Water Treatment', value: 'water_treatment' },
          { id: 'drainage', name: 'Drainage Systems', value: 'drainage' },
          { id: 'pipe_installation', name: 'Pipe Installation', value: 'pipe_installation' },
          { id: 'sewage', name: 'Sewage Systems', value: 'sewage' },
          { id: 'water_heating', name: 'Water Heating Systems', value: 'water_heating' },
        ]}
        disabled={submitting}
        renderItem={renderSimpleItem}
      />
    </div>
  );

  // Fields specific to Cleaning services
  const renderCleaningFields = () => (
    <>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Cleaning Type
        </label>
        <Dropdown
          value={form.cleaning_type || ''}
          onValueChange={(value) => onFieldChange('cleaning_type', value)}
          items={[
            { id: 'residential', name: 'Residential', value: 'residential' },
            { id: 'commercial', name: 'Commercial', value: 'commercial' },
            { id: 'deep_cleaning', name: 'Deep Cleaning', value: 'deep_cleaning' },
            { id: 'regular', name: 'Regular Cleaning', value: 'regular' },
            { id: 'move_in_out', name: 'Move In/Out', value: 'move_in_out' },
            { id: 'laundry', name: 'Laundry Service', value: 'laundry' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Team Size
          </label>
          <Input
            type="number"
            placeholder="e.g., 2"
            value={form.team_size || ''}
            onChange={(e) => onFieldChange('team_size', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cleaning Duration (hours)
          </label>
          <Input
            type="number"
            placeholder="e.g., 3"
            value={form.cleaning_duration || ''}
            onChange={(e) => onFieldChange('cleaning_duration', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>
    </>
  );

  // Fields specific to Health & Fitness services
  const renderHealthFields = () => (
    <>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Service Focus
        </label>
        <Dropdown
          value={form.service_focus || ''}
          onValueChange={(value) => onFieldChange('service_focus', value)}
          items={[
            { id: 'personal_training', name: 'Personal Training', value: 'personal_training' },
            { id: 'yoga', name: 'Yoga Instruction', value: 'yoga' },
            { id: 'nutrition', name: 'Nutrition Counseling', value: 'nutrition' },
            { id: 'physical_therapy', name: 'Physical Therapy', value: 'physical_therapy' },
            { id: 'massage', name: 'Massage Therapy', value: 'massage' },
            { id: 'mental_health', name: 'Mental Health', value: 'mental_health' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Session Type
        </label>
        <Dropdown
          value={form.session_type || ''}
          onValueChange={(value) => onFieldChange('session_type', value)}
          items={[
            { id: 'in_person', name: 'In-Person', value: 'in_person' },
            { id: 'online', name: 'Online', value: 'online' },
            { id: 'both', name: 'Both', value: 'both' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>
    </>
  );

  // Fields specific to Education & Tutoring
  const renderEducationFields = () => (
    <>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Subject/Field
        </label>
        <Input
          type="text"
          placeholder="e.g., Mathematics, English, Programming"
          value={form.subject || ''}
          onChange={(e) => onFieldChange('subject', e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Education Level
        </label>
        <Dropdown
          value={form.educational_level || ''}
          onValueChange={(value) => onFieldChange('educational_level', value)}
          items={[
            { id: 'primary', name: 'Primary School', value: 'primary' },
            { id: 'secondary', name: 'Secondary School', value: 'secondary' },
            { id: 'undergraduate', name: 'Undergraduate', value: 'undergraduate' },
            { id: 'graduate', name: 'Graduate', value: 'graduate' },
            { id: 'professional', name: 'Professional', value: 'professional' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>
    </>
  );

  // Pricing section - common for all services
  const renderPricingSection = () => (
    <div className="mt-6 pt-6 border-t border-gray-200">
      <h5 className="text-md font-semibold text-gray-900 mb-4">Pricing & Availability</h5>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Availability *
        </label>
        <Dropdown
          value={form.availability || 'flexible'}
          onValueChange={(value) => onFieldChange('availability', value)}
          items={[
            { id: 'weekdays', name: 'Weekdays', value: 'weekdays' },
            { id: 'weekends', name: 'Weekends', value: 'weekends' },
            { id: 'evenings', name: 'Evenings', value: 'evenings' },
            { id: '24/7', name: '24/7', value: '24/7' },
            { id: 'flexible', name: 'Flexible', value: 'flexible' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Pricing Type *
        </label>
        <Dropdown
          value={form.pricing_type || 'negotiable'}
          onValueChange={(value) => onFieldChange('pricing_type', value)}
          items={[
            { id: 'hourly', name: 'Hourly Rate', value: 'hourly' },
            { id: 'fixed', name: 'Fixed Price', value: 'fixed' },
            { id: 'negotiable', name: 'Negotiable', value: 'negotiable' },
            { id: 'package', name: 'Package', value: 'package' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>

      {form.pricing_type === 'hourly' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hourly Rate (₦)
          </label>
          <Input
            type="number"
            placeholder="e.g., 5000"
            value={form.hourly_rate || ''}
            onChange={(e) => onFieldChange('hourly_rate', e.target.value)}
            disabled={submitting}
          />
        </div>
      )}

      {form.pricing_type === 'fixed' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fixed Price (₦)
          </label>
          <Input
            type="number"
            placeholder="e.g., 25000"
            value={form.fixed_price || ''}
            onChange={(e) => onFieldChange('fixed_price', e.target.value)}
            disabled={submitting}
          />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">
        {serviceCategory === 'repair' ? 'Repair & Maintenance Details' :
         serviceCategory === 'cleaning' ? 'Cleaning Service Details' :
         serviceCategory === 'health' ? 'Health & Fitness Details' :
         serviceCategory === 'education' ? 'Education & Tutoring Details' :
         serviceCategory === 'legal' ? 'Legal & Financial Details' :
         serviceCategory === 'plumbing' ? 'Plumbing & Water Treatment Details' :
         'Service Details'}
      </h4>
      
      {/* Common fields */}
      {renderCommonFields()}

      {/* Category-specific fields */}
      {serviceCategory === 'repair' && renderRepairFields()}
      {serviceCategory === 'plumbing' && renderPlumbingFields()}
      {serviceCategory === 'cleaning' && renderCleaningFields()}
      {serviceCategory === 'health' && renderHealthFields()}
      {serviceCategory === 'education' && renderEducationFields()}
     
      {/* Pricing section */}
      {renderPricingSection()}
    </div>
  );
};

export default ServicesForm;